import React, { ChangeEvent, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  HStack,
  Image,
  Input,
  Radio,
  RadioGroup,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import FieldLabel from './FieldLabel'; // item 322g — the shared field label
import { ALLOWED_FONTS } from '../../models/companyBrand';
import { loadAllLazyBrandFonts } from '../../theme/lazyFonts'; // item 1516
import trimTransparentEdges from '../logoTrim';
import { HEX_PATTERN, isValidHex, isValidOptionalHex } from '../../utils/hexColor';

// re-exported for existing consumers (e.g. BrandWizard) that import the validators here
export { isValidHex, isValidOptionalHex };

const MAX_LOGO_BYTES = 1_500_000;

interface ColorFieldProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  optional?: boolean;
}

/** native color picker + hex text input pair */
export const ColorField: React.FC<ColorFieldProps> = ({
  label, value, onChange, optional,
}) => (
  <FormControl w="auto">
    <FieldLabel>{label}</FieldLabel>
    <HStack>
      <input
        type="color"
        aria-label={`${label} picker`}
        value={value && HEX_PATTERN.test(value) ? value : '#ffffff'}
        onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        style={{
          width: '40px', height: '40px', padding: 0, border: 'none', cursor: 'pointer',
        }}
      />
      <Input
        w="110px"
        size="sm"
        value={value ?? ''}
        placeholder={optional ? 'default' : '#000000'}
        onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
        isInvalid={value !== null && value !== '' && !HEX_PATTERN.test(value ?? '')}
      />
      {optional && value
      && (
        <Button size="xs" variant="ghost" onClick={() => onChange(null)}>
          RESET
        </Button>
      )}
    </HStack>
  </FormControl>
);
ColorField.defaultProps = { optional: false };

interface LogoFieldProps {
  value: string | null;
  onChange: (dataUrl: string | null) => void;
}

/** logo file upload with preview; emits an image data URL */
export const LogoField: React.FC<LogoFieldProps> = ({ value, onChange }) => {
  const toast = useToast();
  const fileInput = useRef<HTMLInputElement>(null);

  const handleFile = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ status: 'error', title: 'Please choose an image file.' });
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast({ status: 'error', title: 'Logo must be under 1.5 MB.' });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const trimmed = await trimTransparentEdges(reader.result as string);
      onChange(trimmed);
    };
    reader.readAsDataURL(file);
  };

  return (
    <FormControl>
      <FieldLabel>Company logo</FieldLabel>
      {/* item 1183 dart#5 (Chad, mobile) — wraps: REPLACE/REMOVE mashed together beside
          the logo at phone width */}
      <Flex align="center" gridGap={4} flexWrap="wrap" sx={{ rowGap: '8px' }}>
        {value
          ? <Image src={value} alt="Company logo" maxH="60px" maxW="200px" objectFit="contain" />
          : <Text fontSize="sm" color="gray.500">No logo uploaded</Text>}
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
        <Button size="sm" variant="outline" onClick={() => fileInput.current?.click()}>
          {value ? 'REPLACE' : 'UPLOAD'}
        </Button>
        {value
        && (
          <Button size="sm" variant="ghost" onClick={() => onChange(null)}>
            REMOVE
          </Button>
        )}
      </Flex>
    </FormControl>
  );
};

interface FontFieldProps {
  value: string;
  onChange: (font: string) => void;
}

/** curated font choice, each option rendered in its own face */
export const FontField: React.FC<FontFieldProps> = ({ value, onChange }) => {
  // item 1516 — Montserrat/Raleway/Lato/Inter are lazy chunks now; pull them all the moment the
  // picker mounts so every radio previews in its own face (Roboto + the RNWY kit are eager).
  useEffect(() => { loadAllLazyBrandFonts(); }, []);
  return (
    <FormControl>
      <FieldLabel>Font</FieldLabel>
      <RadioGroup value={value} onChange={onChange}>
        <Stack>
          {ALLOWED_FONTS.map((font) => (
            <Radio key={font} value={font}>
              <Box as="span" style={{ fontFamily: `'${font}', sans-serif` }}>
                {font}
                {' '}
                — Let&apos;s make business easier.
              </Box>
            </Radio>
          ))}
        </Stack>
      </RadioGroup>
    </FormControl>
  );
};
